
import React, { useState } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useAnalyticsData } from '@/hooks/useAnalyticsData';
import { DATE_RANGES, COLORS } from '@/utils/analytics-helpers';
import AnalyticsHeader from '@/components/analytics/AnalyticsHeader';
import StatCards from '@/components/analytics/StatCards';
import OptimizedAnalyticsTabs from '@/components/analytics/OptimizedAnalyticsTabs';
import AdminCheckWrapper from '@/components/analytics/admin/AdminCheckWrapper';
import DataManagementCard from '@/components/analytics/admin/DataManagementCard';
import ErrorAlert from '@/components/analytics/ErrorAlert';
import RefreshDataButton from '@/components/analytics/RefreshDataButton';
import { exportCSV } from '@/utils/analytics-helpers';
import AuthFilterButtons, { AuthFilter } from '@/components/analytics/AuthFilterButtons';

const Analytics = () => {
  const { user } = useAuth();
  const [selectedRange, setSelectedRange] = useState(7);
  const [cleanupInProgress, setCleanupInProgress] = useState(false);
  const [authFilter, setAuthFilter] = useState<AuthFilter>('all');
  
  return (
    <AdminCheckWrapper>
      {(isAdmin) => (
        <div className="min-h-screen flex flex-col bg-background">
          <Navbar />
          
          <main className="flex-1 py-6 md:py-12 px-4 w-full max-w-7xl mx-auto">
            <AnalyticsHeader 
              selectedRange={selectedRange}
              setSelectedRange={setSelectedRange}
              DATE_RANGES={DATE_RANGES}
            />
            
            <AnalyticsContent 
              selectedRange={selectedRange}
              userId={isAdmin ? user?.id : undefined}
              cleanupInProgress={cleanupInProgress}
              setCleanupInProgress={setCleanupInProgress}
              authFilter={authFilter}
              setAuthFilter={setAuthFilter}
            />
          </main>
          
          <Footer />
        </div>
      )}
    </AdminCheckWrapper>
  );
};

interface AnalyticsContentProps {
  selectedRange: number;
  userId: string | undefined;
  cleanupInProgress: boolean;
  setCleanupInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  authFilter: AuthFilter;
  setAuthFilter: React.Dispatch<React.SetStateAction<AuthFilter>>;
}

const AnalyticsContent = React.memo(({ 
  selectedRange, 
  userId,
  cleanupInProgress,
  setCleanupInProgress,
  authFilter,
  setAuthFilter
}: AnalyticsContentProps) => {
  // Get analytics data with enhanced error handling and loading states
  const {
    // Data
    pageStats,
    actionStats,
    dailyStats,
    platformStats,
    browserStats,
    osStats,
    timeStats,
    languageStats,
    referrerStats,
    sessionMetrics,
    exitPages,
    utmStats,
    totalEvents,
    
    // Global loading state
    loading: dataLoading,
    
    // Individual loading states
    loadingPageStats,
    loadingActionStats,
    loadingDailyStats,
    loadingPlatformStats,
    loadingBrowserStats,
    loadingOSStats,
    loadingTimeStats,
    loadingLanguageStats,
    loadingTotalStats,
    loadingReferrerStats,
    loadingSessionMetrics,
    loadingExitPages,
    loadingUTMStats,
    
    // Errors
    hasErrors,
    errors,
    
    // Functions
    cleanupOldData,
    retryAllData,
    loadData
  } = useAnalyticsData(selectedRange, userId, authFilter);
  
  const handleDataCleanup = React.useCallback(async (timeframe: string) => {
    try {
      setCleanupInProgress(true);
      const deletedCount = await cleanupOldData(timeframe);
      
      if (deletedCount > 0) {
        toast.success(`Successfully cleaned up ${deletedCount} analytics records`);
      } else {
        toast.info('No records found matching the cleanup criteria');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Failed to clean up data');
    } finally {
      setCleanupInProgress(false);
    }
  }, [cleanupOldData, setCleanupInProgress]);

  const handleRefreshData = React.useCallback(() => {
    loadData();
    toast.success('Analytics data refreshed');
  }, [loadData]);

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <AuthFilterButtons selectedFilter={authFilter} setSelectedFilter={setAuthFilter} />
        <RefreshDataButton 
          onRefresh={handleRefreshData}
          isLoading={dataLoading}
        />
      </div>
      
      <ErrorAlert 
        hasErrors={hasErrors}
        onRetryClick={retryAllData}
      />
      
      <StatCards 
        loading={dataLoading}
        loadingPageStats={loadingPageStats}
        loadingPlatformStats={loadingPlatformStats}
        loadingBrowserStats={loadingBrowserStats}
        loadingOSStats={loadingOSStats}
        loadingSessionMetrics={loadingSessionMetrics}
        totalEvents={totalEvents}
        pageStats={pageStats}
        platformStats={platformStats}
        browserStats={browserStats}
        osStats={osStats}
        sessionMetrics={sessionMetrics}
        selectedRange={selectedRange}
      />
      
      <OptimizedAnalyticsTabs 
        pageStats={pageStats}
        actionStats={actionStats}
        dailyStats={dailyStats}
        platformStats={platformStats}
        browserStats={browserStats}
        osStats={osStats}
        timeStats={timeStats}
        languageStats={languageStats}
        referrerStats={referrerStats}
        exitPages={exitPages}
        utmStats={utmStats}
        loadingPageStats={loadingPageStats}
        loadingActionStats={loadingActionStats}
        loadingDailyStats={loadingDailyStats}
        loadingPlatformStats={loadingPlatformStats}
        loadingBrowserStats={loadingBrowserStats}
        loadingOSStats={loadingOSStats}
        loadingTimeStats={loadingTimeStats}
        loadingLanguageStats={loadingLanguageStats}
        loadingReferrerStats={loadingReferrerStats}
        loadingExitPages={loadingExitPages}
        loadingUTMStats={loadingUTMStats}
        exportCSV={exportCSV}
        COLORS={COLORS}
      />

      <DataManagementCard 
        onCleanupData={handleDataCleanup}
        cleanupInProgress={cleanupInProgress}
      />
    </div>
  );
});

AnalyticsContent.displayName = 'AnalyticsContent';

export default Analytics;
